import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, of, shareReplay, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppService } from './app.service';

export interface Tienda {
  /** Es el valor que se guarda en codigoTienda del cliente. */
  storeCode : string;
  nombre : string;
  ciudad? : string;
  calle? : string;
  /** nombre + calle + ciudad, normalizado, para buscar escribiendo cualquiera. */
  buscable : string;
}

/**
 * Lista de tiendas, para el combo "Mi tienda habitual" de Mis datos.
 *
 * Va contra la API de Stores, que es distinta de singleCustomerUrl. Por eso el
 * AuthenticationInterceptor no le agrega las credenciales (solo matchea la URL
 * de single customer) y hay que mandarlas explicitamente acá.
 */
@Injectable()
export class StoresService {

  private cache$? : Observable<Tienda[]>;

  constructor(private http : HttpClient,
              private appService : AppService) {}

  /**
   * La lista es chica (38 tiendas en AR) y no cambia durante la sesion, asi que
   * se pide una sola vez y se comparte con shareReplay.
   */
  getTiendas() : Observable<Tienda[]> {

    if(!this.cache$){

      const headers = new HttpHeaders({
        'X-Api-Key': environment.apikey,
        'X-Api-Secret': environment.apisecret,
        'CountryCode': this.appService.countryCode ?? 'AR'
      });

      const url = `${environment.storesUrl}Store/GetAll?MaxResultCount=500`;

      this.cache$ = this.http.get<any>(url, { headers }).pipe(
        map(respuesta => this.mapear(respuesta)),
        // Si la API de tiendas falla, el combo queda vacío pero la pantalla
        // sigue funcionando: es un campo opcional.
        catchError(() => of([] as Tienda[])),
        shareReplay(1)
      );
    }

    return this.cache$;
  }

  private mapear(respuesta : any) : Tienda[] {

    // El interceptor desenvuelve la respuesta de ABP, asi que puede llegar ya
    // como result o como el envoltorio completo.
    const cuerpo = respuesta?.result ?? respuesta;
    const items : any[] = cuerpo?.items ?? (Array.isArray(cuerpo) ? cuerpo : []);

    return items
      .filter(s => s?.storeCode && s?.name && !s.isWarehouse)
      .map(s => {

        const nombre = String(s.name).trim();
        const ciudad = s.addressCity ? String(s.addressCity).trim() : undefined;

        // La calle se arma con nombre y numero, que vienen separados.
        const calle = [s.addressStreetName, s.addressStreetNumber]
          .filter(x => x !== null && x !== undefined && String(x).trim() !== '')
          .map(x => String(x).trim())
          .join(' ') || undefined;

        return {
          storeCode : String(s.storeCode).trim(),
          nombre,
          ciudad,
          calle,
          buscable : StoresService.normalizar([nombre, calle, ciudad].filter(x => x).join(' '))
        } as Tienda;
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  /**
   * Minusculas y sin acentos, para que "vicente lopez" encuentre
   * "Vicente López" y escribir la calle sin tildes tambien funcione.
   */
  static normalizar(texto : string) : string {
    return (texto ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }
}
